import CheckCircle from '@mui/icons-material/CheckCircle'
import FilterList from '@mui/icons-material/FilterList'
import { default as ImageIcon } from '@mui/icons-material/Image'
import Search from '@mui/icons-material/Search'
import ZoomIn from '@mui/icons-material/ZoomIn'
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'

import DateTimeRangePicker from '@wojtekmaj/react-datetimerange-picker'
import '@wojtekmaj/react-datetimerange-picker/dist/DateTimeRangePicker.css'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { useEffect, useState } from 'react'
import 'react-calendar/dist/Calendar.css'
import 'react-clock/dist/Clock.css'
import { getMPDataWithParams } from 'src/mentor/services'

const PrescriptionImageGallery = ({
  userId,
  onSelectImage,
  onViewImage,
  selectedImages = [],
  appFileSearchParams,
}) => {
  const [appFiles, setAppFiles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const [totalElements, setTotalElements] = useState(0)

  // Filter states
  const [showFilters, setShowFilters] = useState(false)
  const [dateRange, setDateRange] = useState(() => {
    const startDate = appFileSearchParams?.createdTimeFrom
      ? dayjs(appFileSearchParams.createdTimeFrom).toDate()
      : null
    const endDate = appFileSearchParams?.createdTimeTo
      ? dayjs(appFileSearchParams.createdTimeTo).toDate()
      : null

    // Return null if both dates are null, otherwise return the array
    return startDate || endDate ? [startDate, endDate] : null
  })
  const [mediaId, setMediaId] = useState(appFileSearchParams?.mediaId || '')
  const [mediaIdInput, setMediaIdInput] = useState(appFileSearchParams?.mediaId || '')

  console.log({ appFileSearchParams })

  const getFileUrl = (fileId) => {
    return `https://media.aumhum.com/images/prod/${fileId}`
  }

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size'
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return `${Math.round((bytes / Math.pow(1024, i)) * 100) / 100} ${sizes[i]}`
  }

  // Debounced media ID search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setMediaId(mediaIdInput)
    }, 1000)

    return () => clearTimeout(timeoutId)
  }, [mediaIdInput])

  const handleMediaIdInputChange = (event) => {
    const value = event.target.value
    setMediaIdInput(value)
  }

  const clearFilters = () => {
    setDateRange(null)
    setMediaId('')
    setMediaIdInput('')
  }

  const fetchAppFiles = async (pageNumber = 0, reset = false) => {
    try {
      setLoading(true)
      setError(null)

      const searchParams = {
        createdBy: userId,
        page: pageNumber,
        size: 20,
        sort: 'createdTime,asc',
        // Only fetch image files for prescription purposes
        mimeType: 'image/',
      }

      // Add filter parameters if they exist
      if (dateRange && dateRange[0]) {
        searchParams.createdTimeFrom = dayjs(dateRange[0]).format('YYYY-MM-DDTHH:mm:ss.SSSZ')
      }
      if (dateRange && dateRange[1]) {
        searchParams.createdTimeTo = dayjs(dateRange[1]).format('YYYY-MM-DDTHH:mm:ss.SSSZ')
      }
      if (mediaId) {
        searchParams.mediaId = mediaId
      }

      const response = await getMPDataWithParams(
        '/api/secure/cc/support/doctor/app-file/search/page',
        searchParams,
      )

      if (response.status === 200) {
        const { content, last, totalElements: total } = response.data

        // Filter only image files client-side as backup
        const imageFiles = content.filter((file) => file.mimeType?.startsWith('image/'))

        if (reset) {
          setAppFiles(imageFiles)
        } else {
          setAppFiles((prev) => [...prev, ...imageFiles])
        }

        setHasMore(!last)
        setTotalElements(total)
        setPage(pageNumber)
      } else {
        setError('Failed to fetch gallery images')
      }
    } catch (err) {
      setError('An error occurred while fetching gallery images')
      console.error('Gallery fetch error:', err)
    } finally {
      setLoading(false)
    }
  }

  const loadMore = () => {
    if (!loading && hasMore) {
      fetchAppFiles(page + 1, false)
    }
  }

  const handleImageClick = (file) => {
    if (file.mimeType?.startsWith('image/')) {
      onSelectImage(file.fileId)
    }
  }

  const handleImageView = (file, event) => {
    event.stopPropagation()
    if (file.mimeType?.startsWith('image/')) {
      const currentIndex = appFiles.findIndex((f) => f.fileId === file.fileId)
      onViewImage(currentIndex, appFiles)
    }
  }

  const isSelected = (fileId) => (selectedImages || []).includes(fileId)

  useEffect(() => {
    if (userId) {
      fetchAppFiles(0, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  // Trigger refetch when filters change
  useEffect(() => {
    if (userId) {
      fetchAppFiles(0, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, mediaId, userId])

  if (!userId) {
    return (
      <Box p={3}>
        <Alert severity="info">No user ID available to fetch gallery images.</Alert>
      </Box>
    )
  }

  if (loading && appFiles.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle1">Click to select, zoom icon to view</Typography>
        <Button
          startIcon={<FilterList />}
          onClick={() => setShowFilters(!showFilters)}
          variant="outlined"
          size="small"
        >
          Filters
        </Button>
      </Box>

      <Collapse in={showFilters}>
        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Date Range Section */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Date Range Filter
              </Typography>
              <Box sx={{ '& .react-datetimerange-picker': { width: '100%' } }}>
                <DateTimeRangePicker
                  onChange={setDateRange}
                  value={dateRange}
                  clearIcon={null}
                  calendarIcon={null}
                  rangeDivider=" to "
                  format="dd/MM/yyyy h:mm a"
                  locale="en-US"
                />
              </Box>
            </Box>

            {/* Media ID Section */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Media ID Filter
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  value={mediaIdInput}
                  onChange={handleMediaIdInputChange}
                  size="small"
                  placeholder="Search by media ID..."
                  sx={{ flexGrow: 1 }}
                  InputProps={{
                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
                <Button onClick={clearFilters} variant="outlined" size="small">
                  Reset Filters
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Collapse>
      {appFiles.length === 0 ? (
        <Box p={3}>
          <Typography variant="h6" color="textSecondary" textAlign="center">
            No images found in gallery.
          </Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={2}>
            {appFiles.map((file) => (
              <Grid item xs={12} sm={6} key={file.fileId}>
                <Box sx={{ width: '100%', height: '100%' }}>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    sx={{ width: '100%', height: '100%' }}
                    badgeContent={
                      isSelected(file.fileId) ? (
                        <CheckCircle color="success" sx={{ fontSize: 24 }} />
                      ) : null
                    }
                  >
                    <Card
                      sx={{
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        border: isSelected(file.fileId) ? '2px solid #4caf50' : '1px solid #e0e0e0',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 3,
                        },
                      }}
                      onClick={() => handleImageClick(file)}
                    >
                      <CardMedia
                        component="img"
                        height="150"
                        image={getFileUrl(file.fileId)}
                        alt={file.fileName || 'Gallery image'}
                        sx={{
                          objectFit: 'cover',
                          backgroundColor: '#f5f5f5',
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none'
                          e.target.nextSibling.style.display = 'flex'
                        }}
                      />

                      {/* Fallback for broken images */}
                      <Box
                        sx={{
                          height: 150,
                          display: 'none',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          backgroundColor: '#f8f9fa',
                          color: 'text.secondary',
                        }}
                      >
                        <ImageIcon />
                        <Typography variant="caption" mt={1}>
                          Image unavailable
                        </Typography>
                      </Box>

                      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                        <Typography
                          variant="caption"
                          component="h3"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '0.75rem',
                          }}
                        >
                          {file.fileName || 'Unnamed file'}
                        </Typography>

                        <Typography variant="caption" color="text.secondary" display="block">
                          {formatFileSize(file.fileSize)}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          display="block"
                          sx={{
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            '&:hover': {
                              color: 'primary.main',
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation()
                            const fileUrl = getFileUrl(file.fileId);
                            navigator.clipboard.writeText(fileUrl).then(() => {
                              // Optional: You could add a toast notification here
                              toast.success('File URL copied to clipboard')
                              console.log('File URL copied to clipboard:', fileUrl);
                            }).catch(err => {
                              console.error('Failed to copy URL:', err);
                            });
                          }}
                        >
                          Media ID: {file.fileId}
                        </Typography>

                        {file.width && file.height && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {file.width} × {file.height}
                          </Typography>
                        )}
                      </CardContent>

                      <CardActions sx={{ pt: 0, justifyContent: 'space-between' }}>
                        <Tooltip title="View image">
                          <IconButton
                            onClick={(e) => handleImageView(file, e)}
                            size="small"
                            color="primary"
                          >
                            <ZoomIn fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: '0.65rem' }}
                        >
                          {new Date(file.createdTime).toLocaleDateString()}
                        </Typography>
                      </CardActions>
                    </Card>
                  </Badge>
                </Box>
              </Grid>
            ))}
          </Grid>

          {hasMore && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Box
                component="button"
                onClick={loadMore}
                disabled={loading}
                sx={{
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  padding: '8px 16px',
                  backgroundColor: 'transparent',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                {loading ? <CircularProgress size={20} /> : 'Load More'}
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

export default PrescriptionImageGallery
